import { FEEDBACK_BOARD_FIELD_NAMES, FEEDBACK_STATUS } from '../../interfaces/feedback'
import { TaskStatus } from '../../interfaces/task'
import { FilterFieldType, IFilter } from './types'

// The list joins each submission's ACT task as `actTask` (deleted ones too) and its
// assignee as `assignee`, so the columns below read the same values the rows show.
// Quoted aliases keep TypeORM's alias→column rewrite away from these expressions.

// A deleted task reads as Complete and no task as "Ticket not available", as the rows do.
export const FEEDBACK_STATUS_SQL = `CASE WHEN "actTask"."id" IS NULL THEN '${FEEDBACK_STATUS.NO_TICKET}' WHEN "actTask"."deleted_at" IS NOT NULL THEN '${TaskStatus.Complete}' ELSE "actTask"."status" END`

// The task's Type option when set, else the submission's own category — what the column shows.
export const FEEDBACK_CATEGORY_SQL = `COALESCE((SELECT opt->>'name' FROM task_custom_field_values v JOIN custom_field_definitions d ON d.id = v.custom_field_definition_id CROSS JOIN LATERAL jsonb_array_elements(d.options) opt WHERE v.task_id = "actTask"."id" AND d.name = '${FEEDBACK_BOARD_FIELD_NAMES.type}' AND opt->>'id' = (v.selected_option_ids)[1]::text LIMIT 1), "feedback"."category")`

export const FEEDBACK_SUBMITTER_NAME_SQL = `NULLIF(TRIM(CONCAT_WS(' ', "user"."first_name", "user"."last_name")), '')`

export const FEEDBACK_ASSIGNEE_NAME_SQL = `NULLIF(TRIM(CONCAT_WS(' ', "assignee"."first_name", "assignee"."last_name")), '')`

export const FEEDBACK_COMPLETED_AT_SQL = `COALESCE("actTask"."completed_at", "actTask"."deleted_at")`

export const FEEDBACK_MY_FEEDBACK_QUICK_FILTER = 'My Feedback'

const label = (value: string) => {
  const words = value.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const FEEDBACK_LIST_FILTERS_CONFIG = [
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: FEEDBACK_MY_FEEDBACK_QUICK_FILTER,
        // Filled in at render time with the current user's id (see MyFeedbackPage).
        filters: { userId: [] },
      },
    ],
  },
  {
    field: 'title',
    type: FilterFieldType.Text,
    dbField: 'title',
    alias: 'feedback',
    isSortable: true,
    title: 'Task',
    placeholder: 'Task name',
  },
  {
    // The Submitter column sorts by name; the `userId` entry below is its filter.
    field: 'submitterName',
    type: FilterFieldType.Text,
    dbField: FEEDBACK_SUBMITTER_NAME_SQL,
    isSortable: true,
    excludeFilter: true,
    title: 'Submitter',
  },
  {
    field: 'category',
    type: FilterFieldType.List,
    dbField: FEEDBACK_CATEGORY_SQL,
    isSortable: true,
    isMultiple: true,
    title: 'Category',
    options: [
      ...['Major Bug', 'Minor Bug', 'Enhancement'],
      ...['bug', 'feature_request', 'general'],
    ].map((value) => ({ value, label: label(value) })),
  },
  {
    field: 'priority',
    type: FilterFieldType.List,
    dbField: 'priority',
    alias: 'feedback',
    isSortable: true,
    isMultiple: true,
    title: 'Priority',
    options: ['low', 'medium', 'high', 'critical'].map((value) => ({ value, label: label(value) })),
  },
  {
    // The Assignee column sorts by name; the `assignedToUserId` entry below is its filter.
    field: 'assigneeName',
    type: FilterFieldType.Text,
    dbField: FEEDBACK_ASSIGNEE_NAME_SQL,
    isSortable: true,
    excludeFilter: true,
    title: 'Assignee',
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    dbField: FEEDBACK_STATUS_SQL,
    isSortable: true,
    isMultiple: true,
    title: 'Status',
    options: [...Object.values(TaskStatus), FEEDBACK_STATUS.NO_TICKET].map((value) => ({
      value,
      label: value,
    })),
  },
  {
    field: 'createdAt',
    type: FilterFieldType.Date,
    dbField: 'created_at',
    alias: 'feedback',
    isSortable: true,
    title: 'Submitted',
    align: 'right',
  },
  {
    field: 'completedAt',
    type: FilterFieldType.Date,
    dbField: FEEDBACK_COMPLETED_AT_SQL,
    isSortable: true,
    title: 'Completed',
    align: 'right',
  },
  {
    // Filter-only: a user picker whose options the page fills at render time.
    field: 'userId',
    type: FilterFieldType.Autocomplete,
    dbField: 'user_id',
    alias: 'feedback',
    isMultiple: true,
    excludeColumn: true,
    excludeFullSearchFilter: true,
    title: 'Submitter',
    placeholder: 'Submitter',
  },
  {
    field: 'assignedToUserId',
    type: FilterFieldType.Autocomplete,
    dbField: 'assigned_to_user_id',
    alias: 'actTask',
    isMultiple: true,
    excludeColumn: true,
    excludeFullSearchFilter: true,
    title: 'Assignee',
    placeholder: 'Assignee',
  },
] as const satisfies IFilter[]
